import React, { FC, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import axios from '../../api/axios';
import Lead from '../../@types/Lead';
import { Map, AdvancedMarker, Pin, InfoWindow, useAdvancedMarkerRef, useMap } from '@vis.gl/react-google-maps';
import useGoogleMapsLibrary from '../../hooks/useGoogleMapsLibrary';
import { Box, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import Address, { AddressWMetaData } from '../../@types/Address';
import LoadingIndicator from '../common/LoadingIndicator/LoadingIndicator';
import useAuth from '../../hooks/useAuth';
import { isMadJaxAdmin } from '../../utils/isMadJaxAdmin';
import { debounce } from 'lodash';
import StarRateIcon from '@mui/icons-material/StarRate';
import assets from '../../assets';
import LoadingButton from '../common/LoadingButton/LoadingButton';
import Button from '../common/Button';
import { useNavigate } from 'react-router-dom';


const MadJaxHQAddress: AddressWMetaData = {
  metaData: undefined,
  address: {
    zip: "32226",
    dealerId: "NIVEL",
    countryCode: "US",
    lng: -81.5690406,
    addressName: "MadJax HQ",
    country: "United States",
    state: "Florida",
    authorizedVehicleDealerLocation: true,
    city: "Jacksonville",
    stateCode: "FL",
    authorizedAccessoriesDealerLocation: true,
    addressFormatted: "3510 Port Jacksonville Pkwy, Jacksonville, FL 32226, USA",
    sk: "ADDRESS#01K1G9F2952NW044ZETVX45WKY",
    street2: "",
    pk: "DEALERID#NIVEL",
    lat: 30.4495565,
    street: "3510-1 Port Jacksonville Pkwy",
    phone: "(800) 557-9541"
  },
  distanceMiles: 0
}

interface DealerLocationMapProps {

  zipCode?: string,

  lead?: Lead

  authorizedVehicleDealerLocation?: boolean;

  authorizedAccessoriesDealerLocation?: boolean;

  showPriorityDealersOnly?: boolean;

  mapHeight?: number,

  reassignLead?: (dealer: AddressWMetaData) => void;

  reassignmentLoading?: boolean,

  dealerId?: string,

  maxRadiusMiles?: number,

  dealerDisplayMap?: boolean

}

const DealerLocationMap: FC<DealerLocationMapProps> = ({
  zipCode,
  lead,
  authorizedVehicleDealerLocation = true,
  authorizedAccessoriesDealerLocation = false,
  showPriorityDealersOnly = false,
  mapHeight = 800,
  reassignLead,
  reassignmentLoading,
  dealerId,
  maxRadiusMiles = 250,
  dealerDisplayMap = false
}) => {

  const map = useMap();

  const defaultCenter: google.maps.LatLngLiteral = { lat: 39.8283, lng: -98.5795 };

  const defaultZoom: number = 5;

  const [selectedAddress, setSelectedAddress] = useState<AddressWMetaData | undefined>(undefined);

  const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral | undefined>(defaultCenter);

  const [mapZoom, setMapZoom] = useState<number>(defaultZoom);

  const [pois, setPois] = useState<Poi[]>([]);

  const [loading, setLoading] = useState<boolean>(true);

  const { geocodeZipcode } = useGoogleMapsLibrary();

  const [prospectAddress, setProspectAddress] = useState<AddressWMetaData | undefined>(undefined);

  const [addresses, setAddressses] = useState<AddressWMetaData[]>([]);

  const fetchAddresses = useCallback(async (signal: AbortSignal) => {

    try {

      setPois([]);

      setAddressses([]);

      setSelectedAddress(undefined);

      const queryParams: any = {};

      setLoading(true);

      queryParams.authorizedVehicleDealerLocation = authorizedVehicleDealerLocation;

      queryParams.authorizedAccessoriesDealerLocation = authorizedAccessoriesDealerLocation;

      queryParams.priorityDealerOnly = showPriorityDealersOnly;

      if (lead || zipCode) {

        queryParams.distanceRadius = maxRadiusMiles;

        if (lead) {

          queryParams.lat = lead?.address?.lat;

          queryParams.lng = lead?.address?.lng;

          setMapCenter({ lat: queryParams.lat, lng: queryParams.lng });

          setMapZoom(10);

        }

        if (zipCode) {

          const results = await geocodeZipcode(zipCode);

          if (results) {

            queryParams.lat = results.lat;

            queryParams.lng = results.lng;

            setMapCenter({ lat: queryParams.lat, lng: queryParams.lng });

            setMapZoom(10);

          }

        }

      } else {

        setMapCenter(defaultCenter);

        setMapZoom(defaultZoom);

      }

      if (dealerId) {

        queryParams.dealerId = dealerId;

      }

      const response = await axios.get('/dealers/addresses', { signal: signal, params: queryParams });

      const addresses: AddressWMetaData[] = response.data.data as AddressWMetaData[];

      setAddressses(addresses);

      let pois: Poi[] = [];

      for (const address of addresses) {

        const isAssignedLocation = address.address.pk === lead?.dealer?.address?.pk && address.address.sk === lead?.dealer?.address?.sk;

        pois.push({

          key: address?.address?.sk ?? '',

          label: address.address.dealerId === 'ACCONLYDEALER' ? address.address.addressName ?? '' : address?.metaData?.attributes.companyName ?? '',

          location: { lat: address.address.lat ?? 0, lng: address.address.lng ?? 0 },

          address: address,

          isProspect: false,

          isAssignedDealerLocation: isAssignedLocation

        })

      }

      if (lead) {

        pois.push({

          key: `${lead.firstName} ${lead.lastName} (Prospect)`,

          label: `${lead.firstName} ${lead.lastName} (Prospect)`,

          location: { lat: lead?.address?.lat ?? 0, lng: lead?.address?.lng ?? 0 },

          address: { metaData: undefined, address: { pk: 'prospect', sk: 'prospect', ...lead.address as Address }, distanceMiles: 0 },

          isProspect: true,

          isAssignedDealerLocation: false,

        });

        const prospectAddress: AddressWMetaData = { metaData: undefined, address: { pk: 'prospect', sk: 'prospect', ...lead.address as Address }, distanceMiles: 0 };

        setProspectAddress(prospectAddress);

        setSelectedAddress(prospectAddress);

      }

      if (!pois.length) {

        pois.push({

          key: MadJaxHQAddress?.address?.sk ?? '',

          label: MadJaxHQAddress.address.addressName ?? '',

          location: { lat: MadJaxHQAddress.address.lat ?? 0, lng: MadJaxHQAddress.address.lng ?? 0 },

          address: MadJaxHQAddress,

          isProspect: false,

          isAssignedDealerLocation: false

        })

      }

      setPois(pois);

    } catch (error: any) {

      if (error?.code === 'ERR_CANCELED') return;

      console.log(error);

    } finally {

      setLoading(false);

    }

  }, [
    zipCode,
    lead,
    dealerId,
    authorizedAccessoriesDealerLocation,
    authorizedVehicleDealerLocation,
    showPriorityDealersOnly
  ]);

  const fetchAddressesDebounced = useMemo(() => debounce(fetchAddresses, 500), [fetchAddresses]);

  useEffect(() => {

    const controller = new AbortController();

    const signal = controller.signal;

    fetchAddressesDebounced(signal);

    return () => controller.abort();

  }, [
    zipCode,
    lead,
    dealerId,
    authorizedAccessoriesDealerLocation,
    authorizedVehicleDealerLocation,
    showPriorityDealersOnly
  ]);

  /*
    Handle setting map zoom and center based on 
    state
  */
  useEffect(() => {

    if (map) {

      if (mapCenter)
        map.setCenter(mapCenter);

      if (mapZoom)
        map.setZoom(mapZoom);

    }

  }, [
    map,
    mapCenter,
    mapZoom
  ]);

  useEffect(() => {

    if (selectedAddress) {

      setMapCenter({ lat: selectedAddress.address.lat ?? 0, lng: selectedAddress.address.lng ?? 0 });

      setMapZoom(10);

      scrollLocationIntoView(`location-${selectedAddress.address.sk}`);

    } else {

      setMapCenter(defaultCenter);

      setMapZoom(defaultZoom);

    }

  }, [
    selectedAddress,
    setMapCenter,
    setMapZoom
  ]);

  const scrollLocationIntoView = (locationId: string) => {

    const element = document.getElementById(locationId);

    if (element) {

      const locationsContainer = document.getElementById('locationsContainer');

      locationsContainer?.scrollTo({

        behavior: 'smooth',

        top: element.offsetTop - locationsContainer.offsetTop

      });

    }

  }

  return (


    <Stack
      direction="row"
      spacing={1}
      useFlexGap
      flexWrap="wrap-reverse">

      <Stack
        sx={(theme) => ({
          maxWidth: '375px',
          minWidth: '375px',
          maxHeight: `${mapHeight}px`,
          overflow: 'hidden',
          [theme.breakpoints.down('tabletL')]: {
            width: '100%',
            maxWidth: '100%'
          }
        })}>

        <Stack
          id="locationsContainer"
          spacing={2}
          direction="column"
          sx={{
            overflow: 'auto',
            height: 'fit-content',
            maxHeight: `${mapHeight}px`,
            width: '100%'
          }}>

          {loading

            ? (Array(5).fill(null).map((value, index) => (

              <Stack
                key={index}
                direction="row"
                spacing={2}
                sx={{
                  width: '100%',
                }}>

                <Box
                  sx={{
                    width: '100%',
                    cursor: 'pointer',
                    border: '2px solid grey',
                    padding: '12px',
                  }}>

                  <Stack direction="column" spacing={3}>

                    <Skeleton animation="wave" width={'100%'} height={20} />

                    <Stack direction='column' spacing={2}>

                      <Skeleton animation="wave" width={'100%'} height={20} />

                      <Skeleton animation="wave" width={'100%'} height={20} />

                      <Skeleton animation="wave" width={'100%'} height={20} />

                    </Stack>

                  </Stack>

                </Box>

              </Stack>

            )))

            : (addresses.length > 0

              ? (

                <>
                  {prospectAddress &&

                    <Location
                      address={prospectAddress}
                      lead={lead}
                      zipCode={zipCode}
                      selectedAddress={selectedAddress}
                      setSelectedAddress={setSelectedAddress}
                      isProspect={true} />

                  }

                  {addresses.map((address, index) =>

                    <Location
                      key={index}
                      address={address}
                      lead={lead}
                      zipCode={zipCode}
                      selectedAddress={selectedAddress}
                      setSelectedAddress={setSelectedAddress} />

                  )}

                </>

              )

              : (
                <>

                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                      width: '100%',
                    }}>

                    <Box
                      sx={{
                        width: '100%',
                        cursor: 'pointer',
                        border: '2px solid grey',
                        padding: '12px',
                      }}>
                      
                      {dealerDisplayMap ? (<>No authorized addresses for Dealer ID#: {dealerId}</>) : (<>No MadJax Dealers near your area</>)}
                      

                    </Box >

                  </Stack >

                  <Location
                    key={'madjax-hq'}
                    address={MadJaxHQAddress}
                    lead={lead}
                    zipCode={zipCode}
                    selectedAddress={selectedAddress}
                    setSelectedAddress={setSelectedAddress} />

                </>

              ))}

        </Stack>

      </Stack>

      <DealerMapLoadingOverlay
        loading={loading}
        mapHeight={mapHeight}>

        <Map
          mapId="locations"
          defaultZoom={mapZoom}
          defaultCenter={mapCenter}
          disableDefaultUI={true}
          style={{
            width: '100%',
            height: `${mapHeight}px`,
            minHeight: '400px',
          }}>

          <Markers
            pois={pois}
            lead={lead}
            zipCode={zipCode}
            selectedAddress={selectedAddress}
            setSelectedAddress={setSelectedAddress}
            reassignLead={reassignLead}
            reassignmentLoading={reassignmentLoading} />

        </Map>

      </DealerMapLoadingOverlay>

    </Stack>

  );

}

interface DealerMapLoadingOverlayProps {
  loading: boolean
  mapHeight: number,
}

const DealerMapLoadingOverlay: FC<PropsWithChildren<DealerMapLoadingOverlayProps>> = ({
  loading,
  mapHeight,
  children
}) => {

  return (

    <Box sx={(theme) => ({
      display: 'flex',
      position: 'relative',
      justifyContent: 'center',
      alignItems: 'center',
      flexGrow: 8,
      [theme.breakpoints.down('tabletL')]: {
        width: '100%'
      }
    })}>

      <LoadingIndicator
        sx={{ position: 'absolute', zIndex: loading ? 3 : 0 }}
        size={100}
        loading={loading} />

      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: `${mapHeight}px`,
          top: 0,
          left: 0,
          backgroundColor: 'grey',
          zIndex: loading ? 2 : 0,
          opacity: loading ? 0.35 : 0
        }} />

      {children}

    </Box>

  )

}

interface LocationProps {

  address: AddressWMetaData,

  lead: Lead | undefined,

  zipCode: string | undefined,

  selectedAddress: AddressWMetaData | undefined

  setSelectedAddress: React.Dispatch<AddressWMetaData | undefined>

  isProspect?: boolean,

}

const Location: FC<LocationProps> = ({
  address,
  lead,
  zipCode,
  selectedAddress,
  setSelectedAddress,
  isProspect = false
}) => {

  const { user } = useAuth();

  const isAssignedLocation = address.address.pk === lead?.dealer?.address?.pk && address.address.sk === lead?.dealer?.address?.sk;

  return (

    <Stack
      id={`location-${address.address.sk}`}
      direction="row"
      spacing={2}
      sx={{
        width: '100%',
      }}>

      <Box
        sx={{
          width: '100%',
          cursor: 'pointer',
          border: '2px solid grey',
          padding: '12px',
          ...((selectedAddress?.address.sk === address?.address.sk) && {
            border: '2px solid',
            borderImage: 'linear-gradient(to right, #931216, #E20B23, #FF8230) 1'
          })
        }}
        onClick={() => setSelectedAddress(address)}>

        <Stack direction="column" spacing={3}>

          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            alignItems="center">

            {isProspect

              ? (

                <Typography
                  variant='primaryFont'
                  fontSize={18}>

                  {lead?.firstName} {lead?.lastName} (Prospect)

                </Typography>)

              : (


                <Typography
                  variant='primaryFont'
                  fontSize={18}>

                  {address?.metaData?.attributes.companyName ?? address?.address?.addressName}

                </Typography>

              )

            }

            {isProspect &&

              <Tooltip title="Lead Prospect">

                <img src={assets.images.legendProspectPin} width={15} />

              </Tooltip>

            }

            {(!isProspect && isAssignedLocation) &&

              <Tooltip title="Assigned Dealer">

                <img src={assets.images.legendAssignedDealerPin} width={15} />

              </Tooltip>

            }

            {(!isProspect && !isAssignedLocation) &&

              <Tooltip title="Dealer Location">

                <img src={assets.images.legendDealerPin} width={15} />

              </Tooltip>

            }

            {(isMadJaxAdmin(user) && address?.metaData?.attributes?.priorityDealer) &&

              <Tooltip title={'Priority Leads Dealer'}>

                <StarRateIcon sx={{ color: '#EFBF04', width: '22px', height: '22px' }} />

              </Tooltip>

            }

            {(isMadJaxAdmin(user) && !isProspect) &&

              <Typography variant='primaryFont'>({address.address.dealerId})</Typography>

            }

          </Stack>

          <Stack direction="row" spacing={1}>

            <Stack direction='column' spacing={2}>

              <Stack direction="column" spacing={1}>

                {address?.address.authorizedAccessoriesDealerLocation &&

                  <Typography
                    variant="secondaryFont"
                    fontSize={14}
                    sx={{ textTransform: 'uppercase' }}>

                    {address?.address.authorizedAccessoriesDealerLocation ? 'Authorized Accessories Dealer' : ''}

                  </Typography>

                }

                {address?.address.authorizedVehicleDealerLocation &&

                  <Typography
                    variant="secondaryFont"
                    fontSize={14}
                    sx={{ textTransform: 'uppercase' }}>

                    {address?.address.authorizedVehicleDealerLocation ? 'Authorized Vehicle Dealer' : ''}

                  </Typography>

                }

              </Stack>

              <Typography variant='secondaryFont'>{address?.address?.addressFormatted ?? address?.address?.zip}</Typography>

              {address?.address?.phone &&

                <Typography variant='secondaryFont'>{address?.address?.phone}</Typography>

              }

            </Stack>

            {((lead || zipCode) && !isProspect) &&

              <Box
                sx={{
                  maxWidth: '98px',
                  maxHeight: '98px',
                  minWidth: '98px',
                  minHeight: '98px',
                  background: 'linear-gradient(to right, #931216, #E20B23, #FF8230)',
                  borderRadius: '50%',
                  position: 'relative'
                }}>

                <Box sx={{
                  width: '94px',
                  height: '94px',
                  background: 'white',
                  borderRadius: '50%',
                  color: '#E20B23',
                  position: 'absolute',
                  top: '2px',
                  bottom: '2px',
                  left: '2px',
                  right: '2px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column'
                }}>

                  <Typography variant='primaryFont' fontSize={18}>{address?.distanceMiles.toFixed(1)}</Typography>

                  <Typography fontSize={14} sx={{ color: 'black' }}>Miles</Typography>

                </Box>

              </Box>

            }

          </Stack>

        </Stack>


      </Box >

    </Stack >

  )

}

interface MarkersProps {

  pois: Poi[];

  lead?: Lead,

  zipCode?: string,

  selectedAddress: AddressWMetaData | undefined

  setSelectedAddress: React.Dispatch<AddressWMetaData | undefined>

  reassignLead?: (dealer: AddressWMetaData) => void;

  reassignmentLoading?: boolean

}

interface Poi {

  key: string,

  label: string,

  location: google.maps.LatLngLiteral,

  address: AddressWMetaData,

  isProspect: boolean,

  isAssignedDealerLocation: boolean

}

const Markers: FC<MarkersProps> = ({
  pois,
  selectedAddress,
  setSelectedAddress,
  lead,
  zipCode,
  reassignLead,
  reassignmentLoading }) => {

  return (

    <>

      {pois.map((poi: Poi) => (

        <Marker
          key={poi.key}
          poi={poi}
          setSelectedAddress={setSelectedAddress}
          selectedAddress={selectedAddress}
          lead={lead}
          zipCode={zipCode}
          reassignLead={reassignLead}
          reassignmentLoading={reassignmentLoading} />

      ))}

    </>

  )

}

interface MarkerProps {

  poi: Poi,

  lead?: Lead,

  zipCode?: string,

  selectedAddress: AddressWMetaData | undefined,

  setSelectedAddress: React.Dispatch<AddressWMetaData | undefined>

  reassignLead?: (dealer: AddressWMetaData) => void;

  reassignmentLoading?: boolean

}

const Marker: FC<MarkerProps> = ({
  poi,
  lead,
  zipCode,
  selectedAddress,
  setSelectedAddress,
  reassignLead,
  reassignmentLoading
}) => {

  const { user } = useAuth();

  // `markerRef` and `marker` are needed to establish the connection between
  // the marker and infowindow (if you're using the Marker component, you
  // can use the `useMarkerRef` hook instead).
  const [markerRef, marker] = useAdvancedMarkerRef();

  const [pinned, setPinned] = useState<boolean>(false);

  const [infoWindowShown, setInfoWindowShown] = useState<boolean>(false);

  // clicking the marker will toggle the infowindow
  const handleMarkerClick = useCallback(() => {

    setSelectedAddress(poi.address);

    setPinned(true);

    setInfoWindowShown(true);

  }, [poi, setSelectedAddress, setPinned]);

  useEffect(() => {

    setInfoWindowShown(selectedAddress?.address.sk === poi.address.address.sk);

  }, [selectedAddress, setInfoWindowShown]);

  useEffect(() => {

    if (!infoWindowShown) setPinned(false);

  }, [infoWindowShown, setPinned])

  // if the maps api closes the infowindow, we have to synchronize our state
  //const handleClose = useCallback(() => setInfoWindowShown(false), []);

  const navigate = useNavigate();

  return (

    <AdvancedMarker
      position={poi.location}
      title={'Address'}
      ref={markerRef}
      clickable={true}
      onMouseEnter={() => setInfoWindowShown(true)}
      onMouseLeave={() => { if (!pinned) setInfoWindowShown(false) }}
      onClick={handleMarkerClick}>

      {poi.isProspect &&

        <Pin glyph="" background={'blue'} glyphColor={'#FFF'} borderColor={'#000'} />

      }

      {!poi.isProspect

        ? ((poi.isAssignedDealerLocation)

          ? (<Pin glyph="" background={'green'} glyphColor={'#FFF'} borderColor={'#000'} />)

          : (<Pin glyph="" background={'#cb2a20'} glyphColor={'#FFF'} borderColor={'#000'} />))

        : (<></>)

      }

      {infoWindowShown && (

        (

          <InfoWindow
            anchor={marker}
            onClose={() => setInfoWindowShown(false)}
            headerContent={

              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                flexWrap="wrap"
                alignItems="center">

                <Typography variant='primaryFont' fontSize={18}>{poi.label}</Typography>

                {(isMadJaxAdmin(user) && poi.address.metaData?.attributes?.priorityDealer) &&

                  <Tooltip title={'Priority Leads Dealer'}>

                    <StarRateIcon sx={{ color: '#EFBF04', width: '22px', height: '22px' }} />

                  </Tooltip>

                }

                {(isMadJaxAdmin(user) && !poi.isProspect) &&

                  <Typography variant='primaryFont'>({poi.address.address.dealerId})</Typography>

                }

              </Stack>

            }>

            <Stack direction="column" spacing={2}>

              <Stack direction="column" spacing={1}>

                {poi.address?.address.authorizedAccessoriesDealerLocation &&

                  <Typography
                    variant="secondaryFont"
                    fontSize={12}
                    sx={{ textTransform: 'uppercase' }}>

                    {poi.address?.address.authorizedAccessoriesDealerLocation ? 'Authorized Accessories Dealer' : ''}

                  </Typography>

                }

                {poi.address?.address.authorizedVehicleDealerLocation &&

                  <Typography
                    variant="secondaryFont"
                    fontSize={12}
                    sx={{ textTransform: 'uppercase' }}>

                    {poi.address?.address.authorizedVehicleDealerLocation ? 'Authorized Vehicle Dealer' : ''}

                  </Typography>

                }

              </Stack>

              <Typography variant='secondaryFont'>{poi?.address?.address?.addressFormatted ?? poi?.address?.address?.zip}</Typography>

              {((lead || zipCode) && !poi.isProspect) &&

                <Typography variant='secondaryFont'>

                  Distance: {poi.address?.distanceMiles.toFixed(1)} miles from {lead ? `from ${lead.firstName} ${lead.lastName} (Prospect)` : `from ${zipCode}`}

                </Typography>

              }

              {(isMadJaxAdmin(user) && !poi.isProspect) &&

                <Typography variant="primaryFont">Leads: {poi?.address?.metaData?.attributes?.allowLeads ? 'Allowed' : 'Not Allowed'}</Typography>

              }

              {(isMadJaxAdmin(user) && !poi.isProspect) &&

                <Button onClick={() => navigate(`/admin/dealers/${poi.address?.address?.dealerId}`)}>View Dealer</Button>

              }

              {isMadJaxAdmin(user) && (lead && !poi.isProspect && reassignLead && !poi.isAssignedDealerLocation) &&

                <LoadingButton
                  loading={reassignmentLoading}
                  disabled={!poi?.address?.metaData?.attributes?.allowLeads}
                  onClick={(event) => reassignLead(poi?.address) }>
                  Reassign Lead
                </LoadingButton>

              }

            </Stack>

          </InfoWindow>

        )

      )}

    </AdvancedMarker>

  )

}

export default DealerLocationMap;
